import React, { useState, useEffect, useCallback } from 'react'
import { Select, InputNumber, Table, Empty, Spin, Button } from 'antd'
import { ReloadOutlined, UserOutlined } from '@ant-design/icons'
import api from '../../api'

const TIME_RANGES = [
  { label: '1天内', value: '1d' },
  { label: '3天内', value: '3d' },
  { label: '1周内', value: '1w' },
  { label: '1月内', value: '1m' }
]

const SORT_OPTIONS = [
  { label: '播放量', value: 'plays' },
  { label: '点赞数', value: 'likes' },
  { label: '评论数', value: 'comments' }
]

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}

const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

function AccountCell({ record }) {
  const name = record.account_remark || record.account_nickname || record.platform_name || '未命名'
  const avatar = record.account_avatar
  const platform = record.platform
  const platformLabel = PLATFORM_NAMES[platform] || platform || '?'
  const color = PLATFORM_COLORS[platform] || '#999'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', overflow: 'hidden',
          border: '1px solid #e8e8e8', background: '#f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {avatar ? (
            <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <UserOutlined style={{ color: '#bbb', fontSize: 12 }} />
          )}
        </div>
        <div style={{
          position: 'absolute', bottom: -1, right: -1,
          width: 13, height: 13, borderRadius: '50%',
          background: color, border: '1.5px solid #fff',
          fontSize: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 'bold'
        }}>
          {platformLabel[0]}
        </div>
      </div>
      <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>
        {name}
      </span>
    </div>
  )
}

export default function WorksMonitorTab({ selectedAccount }) {
  const [timeRange, setTimeRange] = useState('1w')
  const [minPlays, setMinPlays] = useState(null)
  const [minLikes, setMinLikes] = useState(null)
  const [minComments, setMinComments] = useState(null)
  const [minShares, setMinShares] = useState(null)
  const [sortBy, setSortBy] = useState('plays')
  const [works, setWorks] = useState([])
  const [loading, setLoading] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getWorksMonitor({
        account_id: selectedAccount?.account_id || null,
        time_range: timeRange,
        min_plays: minPlays,
        min_likes: minLikes,
        min_comments: minComments,
        min_shares: minShares,
        sort_by: sortBy
      })
      if (res.success) setWorks(res.works || [])
      else setWorks([])
    } catch {
      setWorks([])
    } finally {
      setLoading(false)
    }
  }, [selectedAccount, timeRange, minPlays, minLikes, minComments, minShares, sortBy])

  useEffect(() => { loadData() }, [loadData])

  const columns = [
    {
      title: '账号名', key: 'account', width: 150, fixed: 'left',
      render: (_, record) => <AccountCell record={record} />
    },
    { title: '作品标题', dataIndex: 'title', key: 'title', width: 200, ellipsis: true },
    {
      title: '发布时间', dataIndex: 'published_at', key: 'published_at', width: 140,
      render: val => {
        if (!val) return '-'
        const d = new Date(val)
        const pad = n => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      }
    },
    {
      title: '播放量', dataIndex: 'plays', key: 'plays', width: 100, align: 'right',
      render: val => (val ?? '-').toLocaleString()
    },
    {
      title: '点赞', dataIndex: 'likes', key: 'likes', width: 90, align: 'right',
      render: val => (val ?? '-').toLocaleString()
    },
    {
      title: '评论', dataIndex: 'comments', key: 'comments', width: 90, align: 'right',
      render: val => (val ?? '-').toLocaleString()
    },
    {
      title: '转发收藏', dataIndex: 'shares', key: 'shares', width: 100, align: 'right',
      render: val => (val ?? '-').toLocaleString()
    }
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', padding: 16 }}>
      <div style={{ flexShrink: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#666' }}>时间：</span>
        <Select value={timeRange} onChange={setTimeRange} options={TIME_RANGES} style={{ width: 100 }} size="small" />

        <span style={{ fontSize: 13, color: '#666' }}>播放量 &ge;</span>
        <InputNumber value={minPlays} onChange={setMinPlays} min={0} placeholder="不限" style={{ width: 100 }} size="small" />

        <span style={{ fontSize: 13, color: '#666' }}>点赞数 &ge;</span>
        <InputNumber value={minLikes} onChange={setMinLikes} min={0} placeholder="不限" style={{ width: 100 }} size="small" />

        <span style={{ fontSize: 13, color: '#666' }}>评论数 &ge;</span>
        <InputNumber value={minComments} onChange={setMinComments} min={0} placeholder="不限" style={{ width: 100 }} size="small" />

        <span style={{ fontSize: 13, color: '#666' }}>转发收藏 &ge;</span>
        <InputNumber value={minShares} onChange={setMinShares} min={0} placeholder="不限" style={{ width: 100 }} size="small" />

        <span style={{ fontSize: 13, color: '#666' }}>排名按</span>
        <Select value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} style={{ width: 100 }} size="small" />

        <div style={{ flex: 1 }} />

        <Button icon={<ReloadOutlined />} size="small" onClick={loadData} type="default">
          刷新
        </Button>
        <span style={{ fontSize: 12, color: '#999', minWidth: 60 }}>
          共 {works.length} 条
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <Spin spinning={loading}>
          {works.length > 0 ? (
            <Table
              columns={columns}
              dataSource={works.map((w, i) => ({ ...w, key: w.work_id || i }))}
              pagination={{ pageSize: 20, showSizeChanger: true, showTotal: total => `共 ${total} 条` }}
              size="small"
              scroll={{ x: 850 }}
            />
          ) : (
            !loading && <Empty description="暂无作品数据" style={{ marginTop: 60 }} />
          )}
        </Spin>
      </div>
    </div>
  )
}
